import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface FaqItem {
  question: string;
  answer: string;
}

const cache = new Map<string, FaqItem[]>();

function parseFaqMarkdown(markdown: string): FaqItem[] {
  const parts = markdown.split(/^## /m).filter((p) => p.trim().length > 0);
  return parts.map((part) => {
    const [firstLine, ...rest] = part.split('\n');
    return {
      question: firstLine.trim(),
      answer: rest.join('\n').trim(),
    };
  });
}

export const useFaqContent = (slug: string) => {
  const { i18n } = useTranslation();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lang = i18n.language;
    const key = `faq-${lang}/${slug}`;

    if (cache.has(key)) {
      setFaqs(cache.get(key)!);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(`${process.env.PUBLIC_URL}/content/${lang}/faq-${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('text/html')) throw new Error('Not markdown');
        return res.text();
      })
      .then((text) => {
        if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')) {
          throw new Error('Not markdown');
        }
        const parsed = parseFaqMarkdown(text);
        cache.set(key, parsed);
        setFaqs(parsed);
      })
      .catch(() => {
        setFaqs([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug, i18n.language]);

  return { faqs, loading };
};
